<template>
  <ui-modal-layout :title="tr('Settings')" :icon="SettingsIcon">
    <div class="flex items-start justify-between gap-3">
      <div class="flex flex-col gap-1">
        <span class="text-base font-semibold text-foreground">{{ tr('New lead notifications') }}</span>
        <span class="text-sm text-muted-foreground">
          {{ tr('We notify you by email about the new leads of this form, so you can get in touch with them as soon as possible.') }}
        </span>
      </div>
      <ui-switch v-model="form.enabled" :aria-label="tr('New lead notifications')" />
    </div>

    <template v-if="form.enabled">
      <div class="flex flex-col gap-1.5">
        <ui-label>{{ tr('Send notification to') }}</ui-label>
        <multi-select
          v-model="form.to"
          :placeholder="tr('Search member')"
          :options="companyMemberOptions"
          :loading="userListIsLoading"
          :disabled="userListIsLoading"
          value-key="id"
          label-key="fullname"
        />
      </div>

      <div class="flex flex-col gap-1.5">
        <ui-label>{{ tr('Additional email addresses') }}</ui-label>
        <multi-text-input
          ref="emailInput"
          v-model="form.emails"
          :placeholder="tr('name@example.com')"
          :add-button-label="tr('Add email address')"
          :validation-rule="isValidEmail"
        />
      </div>

      <div class="grid gap-4 sm:grid-cols-2">
        <div class="flex flex-col gap-1.5">
          <ui-label>{{ tr('Notification frequency') }}</ui-label>
          <ui-select v-model="frequencySelectValue">
            <ui-select-trigger class="w-full" :aria-label="tr('Notification frequency')">
              <ui-select-value />
            </ui-select-trigger>
            <ui-select-content>
              <ui-select-item v-for="option in frequencyOptions" :key="String(option.value)" :value="String(option.value)">
                {{ option.label }}
              </ui-select-item>
            </ui-select-content>
          </ui-select>
        </div>

        <div v-if="form.frequency === DAILY_FREQUENCY" class="flex flex-col gap-1.5">
          <ui-label>{{ tr('Sending time') }}</ui-label>
          <ui-select :model-value="String(form.hour)" @update:model-value="(value) => form.hour = Number(value)">
            <ui-select-trigger class="w-full" :aria-label="tr('Sending time')">
              <ui-select-value />
            </ui-select-trigger>
            <ui-select-content>
              <ui-select-item v-for="option in hourOptions" :key="option.value" :value="String(option.value)">
                {{ option.label }}
              </ui-select-item>
            </ui-select-content>
          </ui-select>
        </div>
      </div>

      <info-text v-if="frequencyTimesText">
        {{ frequencyTimesText }}
      </info-text>

      <label class="flex cursor-pointer items-center gap-2.5 text-sm text-foreground">
        <ui-checkbox v-model="form.includeLeadData" />
        {{ tr('Include the details of the lead in the notification') }}
      </label>

      <div v-if="isRuleListLoading || availableRules.length !== 0" class="flex flex-col gap-1.5">
        <ui-label>{{ tr('Only for the leads of these automations') }}</ui-label>
        <multi-select
          v-model="form.ruleIds"
          :placeholder="form.ruleIds.length === 0 ? tr('Notify about every lead') : tr('Add automation')"
          :options="availableRules"
          :loading="isRuleListLoading"
          :disabled="isRuleListLoading"
          value-key="id"
          label-key="name"
        />
      </div>
    </template>

    <div class="flex flex-col gap-3 border-t border-border/60 pt-5">
      <div class="flex flex-col gap-1">
        <span class="text-base font-semibold text-foreground">{{ tr('Lead statuses to Meta') }}</span>
        <span class="text-sm text-muted-foreground">
          {{ tr('When the status of a lead changes, we send it to this pixel with the Conversions API, so Meta can optimize your ads for the leads that convert. Without a selected pixel, the pixel of the automation the lead came from is used.') }}
        </span>
      </div>
      <ui-select v-model="crmPixelValue">
        <ui-select-trigger class="w-full" :aria-label="tr('Pixel')">
          <ui-select-value />
        </ui-select-trigger>
        <ui-select-content>
          <ui-select-item :value="AUTOMATION_PIXEL">{{ tr('The pixel of the automation') }}</ui-select-item>
          <ui-select-group v-for="adAccount in pixelAdAccounts" :key="adAccount.id">
            <ui-select-label>{{ adAccount.name }}</ui-select-label>
            <ui-select-item
              v-for="pixel in adAccount.pixels"
              :key="adAccount.id + '|' + pixel.id"
              :value="adAccount.id + '|' + pixel.id"
            >
              {{ pixel.name }}
            </ui-select-item>
          </ui-select-group>
        </ui-select-content>
      </ui-select>
    </div>

    <template #footer>
      <ui-button variant="outline" :disabled="isSaving" @click="() => close()">
        {{ tr('Cancel') }}
      </ui-button>
      <ui-button :disabled="isSaving" @click="() => save()">
        <loader-circle-icon v-if="isSaving" class="animate-spin" />
        {{ tr('Save') }}
      </ui-button>
    </template>
  </ui-modal-layout>
</template>

<script>
import { LoaderCircle, Settings } from '@lucide/vue';
import { markRaw } from 'vue';

import CompanyMemberListMixin from '@/mixins/CompanyMemberList.js';
import LeadgenFormService from '@/services/LeadgenForm.js';
import InfoText from '@/ui/components/InfoText.vue';
import MultiSelect from '@/ui/components/MultiSelect.vue';
import MultiTextInput from '@/ui/components/MultiTextInput.vue';
import { isValidEmail } from '@/utils/validators.js';

const DAILY_FREQUENCY = 24 * 60;

// The kit select works with non-empty strings; the pixels are "<ad account id>|<pixel id>".
const AUTOMATION_PIXEL = 'automation';

function crmPixelSelectValue(crmPixel) {
  return crmPixel ? crmPixel.ad_account_id + '|' + crmPixel.pixel_id : AUTOMATION_PIXEL;
}

export default {
  name: 'LeadgenFormSettingsModalContent',
  components: {
    InfoText,
    LoaderCircleIcon: LoaderCircle,
    MultiSelect,
    MultiTextInput,
  },
  mixins: [ CompanyMemberListMixin ],
  props: {
    leadgenForm: {
      type: Object,
      required: true,
    },
  },
  emits: [ 'close' ],
  data() {
    const notifications = this.leadgenForm.notifications ?? {};

    return {
      SettingsIcon: markRaw(Settings),
      DAILY_FREQUENCY,
      AUTOMATION_PIXEL,
      crmPixelValue: crmPixelSelectValue(this.leadgenForm.crm_pixel),
      isSaving: false,
      isRuleListLoading: true,
      availableRules: [],
      form: {
        enabled: !!notifications.enabled,
        to: [ ...(notifications.to ?? []) ],
        emails: [ ...(notifications.emails ?? []) ],
        frequency: notifications.frequency ?? null,
        hour: notifications.hour ?? 8,
        includeLeadData: notifications.include_lead_data ?? true,
        ruleIds: [ ...(notifications.rule_ids ?? []) ],
      },
    };
  },
  computed: {
    // The kit select works with strings ("null" = immediately).
    frequencySelectValue: {
      get() {
        return String(this.form.frequency);
      },
      set(value) {
        this.form.frequency = value === 'null' ? null : Number(value);
      },
    },
    frequencyOptions() {
      // The emails go out at fixed times of the day, so only the steps dividing the day evenly.
      return [ null, 15, 30, 60, 120, 180, 240, 360, 480, 720, DAILY_FREQUENCY ].map(value => ({
        value,
        label: this.frequencyLabel(value),
      }));
    },
    hourOptions() {
      return Array.from({ length: 24 }, (_, hour) => ({ value: hour, label: hour + ':00' }));
    },
    // When the emails go out with the selected frequency; obvious for hourly and daily.
    frequencyTimesText() {
      const frequency = this.form.frequency;
      if (!frequency || frequency === 60 || frequency === DAILY_FREQUENCY) {
        return null;
      }

      if (frequency < 60) {
        const minutes = [];
        for (let minute = 0; minute < 60; minute += frequency) {
          minutes.push(minute + '.');
        }

        return this.tr('Sent in minute :minutes of every hour.', { ':minutes': this.joinList(minutes) });
      }

      if (frequency === 120) {
        return this.tr('Sent at every even hour, in your company\'s timezone.');
      }

      const times = [];
      for (let minutes = 0; minutes < DAILY_FREQUENCY; minutes += frequency) {
        times.push((minutes / 60) + ':00');
      }

      return this.tr('Sent at :times, in your company\'s timezone.', { ':times': this.joinList(times) });
    },
    emails() {
      return this.form.emails.filter(email => !!email);
    },
    // The available pixels by ad account.
    pixelAdAccounts() {
      return (this.$root.currentCompany?.ad_accounts ?? [])
        .filter(adAccount => !adAccount.deleted_at)
        .map(adAccount => ({
          id: adAccount.id,
          name: adAccount.name,
          pixels: adAccount.pixels ?? [],
        }))
        .filter(adAccount => adAccount.pixels.length !== 0);
    },
  },
  created() {
    LeadgenFormService.getById(this.leadgenForm.id)
      .then(leadgenForm => {
        // The selector drops the selected automations that are not among its options, so an
        // automation deleted since is not kept in the filter either.
        this.availableRules = leadgenForm.notification_rules;

        return leadgenForm;
      })
      .catch(err => this.error(err))
      .finally(() => this.isRuleListLoading = false);
  },
  methods: {
    isValidEmail,
    frequencyLabel(minutes) {
      if (minutes === null) {
        return this.tr('Immediately');
      }

      if (minutes === DAILY_FREQUENCY) {
        return this.tr('Every day');
      }

      if (minutes < 60) {
        return this.tr('Every :num minutes', { ':num': minutes });
      }

      const hours = minutes / 60;

      return hours === 1 ? this.tr('Every hour') : this.tr('Every :num hours', { ':num': hours });
    },
    joinList(items) {
      if (items.length < 2) {
        return items.join('');
      }

      return items.slice(0, -1).join(', ') + ' ' + this.tr('and') + ' ' + items[items.length - 1];
    },
    close() {
      this.$emit('close');
    },
    validate() {
      if (!this.form.enabled) {
        return true;
      }

      if (!this.validateSelectedUsers(this.form.to)) {
        return false;
      }

      if (this.$refs.emailInput && !this.$refs.emailInput.validate()) {
        this.error(this.tr('Please enter valid email addresses.'));
        return false;
      }

      if (this.form.to.length === 0 && this.emails.length === 0) {
        this.error(this.tr('Please select at least one recipient (member or email address).'));
        return false;
      }

      return true;
    },
    save() {
      if (this.isSaving || !this.validate()) {
        return;
      }

      this.$toast.clear();
      this.isSaving = true;

      const [ adAccountId, pixelId ] = this.crmPixelValue.split('|');

      LeadgenFormService.updateSettings(this.leadgenForm.id, {
        notifications: {
          enabled: this.form.enabled,
          to: this.form.to,
          emails: this.emails,
          frequency: this.form.frequency,
          hour: this.form.frequency === DAILY_FREQUENCY ? this.form.hour : null,
          includeLeadData: this.form.includeLeadData,
          ruleIds: this.form.ruleIds,
        },
        crmPixel: this.crmPixelValue === AUTOMATION_PIXEL ? null : { adAccountId, pixelId },
      })
        .then(leadgenForm => {
          this.leadgenForm.updateWith({ notifications: leadgenForm.notifications, crm_pixel: leadgenForm.crm_pixel });
          this.$toast.success(this.tr('Settings saved.'));
          this.close();

          return leadgenForm;
        })
        .catch(err => this.error(err))
        .finally(() => this.isSaving = false);
    },
  },
};
</script>
